import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";

declare global {
  interface Window {
    zaraz?: { track?: (name: string, props: Record<string, string>) => void };
  }
}

const THRESHOLDS = [25, 50, 75, 90, 100] as const;

function scrollPercent(): number {
  const doc = document.documentElement;
  const scrollTop = window.scrollY || doc.scrollTop;
  const viewportHeight = window.innerHeight;
  const documentHeight = doc.scrollHeight;
  if (documentHeight <= 0) return 0;
  return ((scrollTop + viewportHeight) / documentHeight) * 100;
}

/**
 * Fires a Zaraz `scroll_depth` event once per threshold (25/50/75/90/100)
 * per page load. Resets on client-side route changes and popstate so each
 * page gets its own set of scroll events. No-ops when Zaraz is absent.
 */
export function ScrollDepthTracker() {
  const router = useRouter();

  useEffect(() => {
    const fired = new Set<number>();
    let ticking = false;

    const check = () => {
      ticking = false;
      if (typeof window.zaraz?.track !== "function") return;
      const pct = scrollPercent();
      for (const threshold of THRESHOLDS) {
        if (pct >= threshold && !fired.has(threshold)) {
          fired.add(threshold);
          try {
            // Zaraz drops non-string property values, which made
            // {{ client.scrollDepth }} resolve empty — send a string.
            window.zaraz.track("scroll_depth", {
              scrollDepth: String(threshold),
            });
          } catch {
            /* tracking must never break the page */
          }
        }
      }
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(check);
      }
    };

    const reset = () => {
      fired.clear();
      // New page may be short enough to cross thresholds immediately.
      onScroll();
    };

    // Zaraz loads asynchronously, so scrolls that happen before it is ready
    // would otherwise be lost. Poll until zaraz.track exists, then run a
    // check so any thresholds already crossed fire (once) at that point.
    const zarazWaiter = window.setInterval(() => {
      if (typeof window.zaraz?.track === "function") {
        window.clearInterval(zarazWaiter);
        check();
      }
    }, 500);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("popstate", reset);
    const unsubscribe = router.subscribe("onResolved", reset);

    // Initial check for short pages / restored scroll positions.
    onScroll();

    return () => {
      window.clearInterval(zarazWaiter);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("popstate", reset);
      unsubscribe();
    };
  }, [router]);

  return null;
}
